import React from "react";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";

// Desktop HeroSection
const HeroDesktop = () => (
  <Box
    sx={{
      position: "relative",
      height: "70vh",
      width: "100vw",
      overflow: "hidden",
      color: "#fff",
      textAlign: "center",
      backgroundImage: "url('homepage/hero.jpg')",
      backgroundSize: "cover",
      backgroundPosition: "center",
      backgroundRepeat: "no-repeat",
      animation: "bgZoomCircular 12s linear infinite",
    }}
  >
    {/* Overlay */}
    <Box
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        background: "linear-gradient(to bottom right, rgba(0,0,0,0.7), rgba(0,0,0,0.5))",
        zIndex: 1,
      }}
    />

    {/* Clouds */}
    <Box
      sx={{
        position: "absolute",  
        top: 0,
        left: 0,
        width: "200%",
        height: "40%",
        backgroundImage: "url('/clouds.png')",
        backgroundSize: "cover",
        backgroundRepeat: "repeat-x",
        animation: "cloudMove 60s linear infinite",
        zIndex: 2,
        pointerEvents: "none",
        opacity: 0.4,
      }}
    />

    {/* Lightning */}
    <Box
      className="lightning"
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        zIndex: 3,
        pointerEvents: "none",
      }}
    />

    {/* Thunder bolts */}
    <svg
      className="bolts"
      viewBox="0 0 800 600"
      xmlns="http://www.w3.org/2000/svg"
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        zIndex: 4,
        pointerEvents: "none",
      }}
    >
      {[...Array(8)].map((_, i) => (
        <g className="bolt" key={i} style={{ animationDelay: `${i * 1.2}s` }}>
          <polyline
            points={`
              ${300 + Math.random() * 200},0
              ${320 + Math.random() * 160},150
              ${340 + Math.random() * 120},300
              ${360 + Math.random() * 80},450
              ${380 + Math.random() * 60},600
            `}
            stroke="rgba(255,255,255,0.9)"
            strokeWidth={2 + Math.random() * 2}
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            filter="url(#glow)"
          />
        </g>
      ))}
      <defs>
        <filter id="glow">
          <feGaussianBlur stdDeviation="4" result="coloredBlur" />
          <feMerge>
            <feMergeNode in="coloredBlur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
    </svg>

    {/* Text */}
    <Box
      sx={{
        zIndex: 5,
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        maxWidth: "80%",
      }}
    >
      <Typography
        variant="h2"
        sx={{
          fontWeight: "bold",
          textShadow: "2px 2px 15px rgba(0,0,0,0.9)",
          mb: 2,
          whiteSpace: "nowrap",
        }}
      >
        WELCOME TO ANAGHA ENGINEERS
      </Typography>
      <Typography
        variant="h6"
        sx={{
          color: "white",
          fontWeight: "500",
          textShadow: "1px 1px 10px rgba(0,0,0,0.7)",
          whiteSpace: "nowrap",
        }}
      >
        Innovative Engineering • On-time Execution • Reliable Solutions • Trusted Partnerships
      </Typography>
    </Box>

    {/* Animations */}
    <style>
      {`
        @keyframes bgZoomCircular {
          0% { background-size: 100% 100%; background-position: 50% 50%; }
          25% { background-size: 110% 110%; background-position: 55% 45%; }
          50% { background-size: 120% 120%; background-position: 50% 50%; }
          75% { background-size: 110% 110%; background-position: 45% 55%; }
          100% { background-size: 100% 100%; background-position: 50% 50%; }
        }

        @keyframes cloudMove {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }

        .lightning::before, .lightning::after {
          content: "";
          position: absolute;
          width: 100%;
          height: 100%;
          background: radial-gradient(circle, rgba(255,255,255,0.6) 0%, transparent 70%);
          opacity: 0;
          animation: flashBig 8s infinite;
        }
        .lightning::after { animation-delay: 4s; }

        @keyframes flashBig {
          0%, 93%, 100% { opacity: 0; }
          94% { opacity: 1; }
          95% { opacity: 0.3; }
          96% { opacity: 1; }
          97% { opacity: 0; }
        }

        .bolt {
          opacity: 0;
          transform: translateY(-50px) rotate(0deg) scale(1);
          animation: boltFlash 6s infinite;
        }

        @keyframes boltFlash {
          0%, 80%, 100% { opacity: 0; transform: translateY(-50px) rotate(0deg) scale(1); }
          81% { opacity: 1; transform: translateY(0) rotate(5deg) scale(1.2); }
          82% { opacity: 0.7; transform: translateY(15px) rotate(-5deg) scale(1.1); }
          83% { opacity: 1; transform: translateY(25px) rotate(3deg) scale(1.2); }
          84% { opacity: 0; transform: translateY(35px) rotate(0deg) scale(1); }
        }
      `}
    </style>
  </Box>
);

// Mobile HeroSection
const HeroMobile = () => (
  <Box
    sx={{
      position: "relative",
      height: "60vh",
      width: "100vw",
      overflow: "hidden",
      color: "#fff",
      textAlign: "left",
      backgroundImage: "url('homepage/hero.jpg')",
      backgroundSize: "cover",
      backgroundPosition: "center",
      backgroundRepeat: "no-repeat",
      animation: "bgZoomCircular 12s linear infinite",
    }}
  >
    {/* Overlay */}
    <Box
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        background: "linear-gradient(to bottom right, rgba(0,0,0,0.7), rgba(0,0,0,0.5))",
        zIndex: 1,
      }}
    />

    {/* Clouds */}
    <Box
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "200%",
        height: "30%",
        backgroundImage: "url('/clouds.png')",
        backgroundSize: "cover",
        backgroundRepeat: "repeat-x",
        animation: "cloudMove 60s linear infinite",
        zIndex: 2,
        pointerEvents: "none",
        opacity: 0.35,
      }}
    />

    {/* Lightning */}
    <Box
      className="lightning"
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        zIndex: 3,
        pointerEvents: "none",
      }}
    />

    {/* Thunder bolts */}
    <svg
      className="bolts"
      viewBox="0 0 800 600"
      xmlns="http://www.w3.org/2000/svg"
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        zIndex: 4,
        pointerEvents: "none",
      }}
    >
      {[...Array(8)].map((_, i) => (
        <g className="bolt" key={i} style={{ animationDelay: `${i * 1.2}s` }}>
          <polyline
            points={`
              ${300 + Math.random() * 200},0
              ${320 + Math.random() * 160},150
              ${340 + Math.random() * 120},300
              ${360 + Math.random() * 80},450
              ${380 + Math.random() * 60},600
            `}
            stroke="rgba(255,255,255,0.9)"
            strokeWidth={2 + Math.random() * 2}
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            filter="url(#glow)"
          />
        </g>
      ))}
      <defs>
        <filter id="glow">
          <feGaussianBlur stdDeviation="4" result="coloredBlur" />
          <feMerge>
            <feMergeNode in="coloredBlur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
    </svg>

    {/* Text */}
    <Box
      sx={{
        zIndex: 5,
        position: "absolute",
        top: "25%",
        left: "3%",
        maxWidth: "95%",
        px: 2,
      }}
    >
      <Typography
        variant="h3"
        sx={{
          fontWeight: "bold",
          textShadow: "2px 2px 15px rgba(0,0,0,0.9)",
          mb: 1,
          fontSize: "1.7rem",
          lineHeight: 1.2,
        }}
      >
        WELCOME TO <br /> ANAGHA ENGINEERS
      </Typography>
      <Typography
        variant="h6"
        sx={{
          color: "white",
          fontWeight: "500",
          textShadow: "1px 1px 10px rgba(0,0,0,0.7)",
          fontSize: "0.95rem",
          lineHeight: 1.4,
        }}
      >
        Innovative Engineering • On-time Execution <br /> • Reliable Solutions • Trusted Partnerships
      </Typography>
    </Box>

    {/* Animations */}
    <style>
      {`
        @keyframes bgZoomCircular {
          0% { background-size: 100% 100%; background-position: 50% 50%; }
          25% { background-size: 110% 110%; background-position: 55% 45%; }
          50% { background-size: 120% 120%; background-position: 50% 50%; }
          75% { background-size: 110% 110%; background-position: 45% 55%; }
          100% { background-size: 100% 100%; background-position: 50% 50%; }
        }

        @keyframes cloudMove {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }

        .lightning::before, .lightning::after {
          content: "";
          position: absolute;
          width: 100%;
          height: 100%;
          background: radial-gradient(circle, rgba(255,255,255,0.6) 0%, transparent 70%);
          opacity: 0;
          animation: flashBig 8s infinite;
        }
        .lightning::after { animation-delay: 4s; }

        @keyframes flashBig {
          0%, 93%, 100% { opacity: 0; }
          94% { opacity: 1; }
          95% { opacity: 0.3; }
          96% { opacity: 1; }
          97% { opacity: 0; }
        }

        .bolt {
          opacity: 0;
          transform: translateY(-50px) rotate(0deg) scale(1);
          animation: boltFlash 6s infinite;
        }

        @keyframes boltFlash {
          0%, 80%, 100% { opacity: 0; transform: translateY(-50px) rotate(0deg) scale(1); }
          81% { opacity: 1; transform: translateY(0) rotate(5deg) scale(1.2); }
          82% { opacity: 0.7; transform: translateY(15px) rotate(-5deg) scale(1.1); }
          83% { opacity: 1; transform: translateY(25px) rotate(3deg) scale(1.2); }
          84% { opacity: 0; transform: translateY(35px) rotate(0deg) scale(1); }
        }
      `}
    </style>
  </Box>
);

// Main HeroSection
const HeroSection = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return isMobile ? <HeroMobile /> : <HeroDesktop />;
};

export default HeroSection;
